import axios from 'axios';
import type {
  PublicReceiptResolution,
  ResolveReceiptCodePayload
} from '../types/public-receipt.types';
import { apiClient } from './client';

/*
 * Receipt and summary PDFs, rendered by the user-portal backend on each download, and the
 * printed receipt link. A download saves the file through an object URL; a refusal rejects
 * with a `ReceiptDownloadError` carrying the backend's status and error code, which the screens
 * turn into a plain message.
 */

/** A download the backend refused, or that never reached it. */
export class ReceiptDownloadError extends Error {
  constructor(
    /** The HTTP status; 0 when no response came back. */
    readonly status: number,
    /** The backend's `error.code`, when it sent one. */
    readonly code: string | null
  ) {
    super(code ?? `Download failed (${status})`);
    this.name = 'ReceiptDownloadError';
  }
}

const readBlob = (
  blob: Blob,
  as: 'readAsText' | 'readAsDataURL'
): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader[as](blob);
  });

/** A blob download's error body is the JSON envelope, still as a Blob. */
const errorCodeOf = async (data: unknown): Promise<string | null> => {
  try {
    const text =
      data instanceof Blob
        ? await readBlob(data, 'readAsText')
        : JSON.stringify(data ?? null);
    const body = JSON.parse(text) as { error?: { code?: unknown } } | null;
    return typeof body?.error?.code === 'string' ? body.error.code : null;
  } catch {
    return null;
  }
};

/** `attachment; filename="x.pdf"` → `x.pdf`; null when the header is absent or unreadable. */
export const filenameFromDisposition = (
  header: string | undefined | null
): string | null => {
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // Fall through to the plain form.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
};

/**
 * How long a saved file's object URL lives. iOS Safari reads the blob after the click returns,
 * so revoking it on the next task loses the file there (WebKit bug 211234); 40 s is the figure
 * FileSaver.js uses.
 */
export const OBJECT_URL_LIFETIME_MS = 40_000;

/**
 * Chrome, Firefox and Edge on iOS run in a WKWebView, which ignores `download` on a `blob:` link
 * but honours it on a `data:` one (WebKit bug 216918).
 */
const IOS_WEBVIEW_BROWSER = /\b(CriOS|FxiOS|EdgiOS)\//;

const clickDownload = (href: string, filename: string) => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const saveBlob = async (blob: Blob, filename: string) => {
  if (IOS_WEBVIEW_BROWSER.test(navigator.userAgent)) {
    clickDownload(await readBlob(blob, 'readAsDataURL'), filename);
    return;
  }
  const url = URL.createObjectURL(blob);
  clickDownload(url, filename);
  setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
};

const fetchAndSave = async (path: string, fallbackName: string) => {
  try {
    const response = await apiClient.get<Blob>(path, {
      responseType: 'blob'
    });
    const filename =
      filenameFromDisposition(response.headers?.['content-disposition']) ??
      fallbackName;
    await saveBlob(response.data, filename);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new ReceiptDownloadError(
        error.response?.status ?? 0,
        await errorCodeOf(error.response?.data)
      );
    }
    throw error;
  }
};

// ── One download per document at a time ──────────────────────────────────────
// A double tap, or a second button for the same file, must not save it twice. While a document
// is being fetched, another request for it resolves at once and does nothing; the first request
// reports the outcome. Screens read the same state to show "Preparing…".

const inFlight = new Set<string>();
const listeners = new Set<() => void>();
const notifyListeners = () => listeners.forEach((listener) => listener());

/** For `useSyncExternalStore`: the listener runs whenever a download starts or ends. */
export const subscribeToDownloads = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Whether the document at `path` (from `receiptDocumentPath`) is being fetched. */
export const isDownloadInFlight = (path: string): boolean => inFlight.has(path);

const downloadPdf = async (path: string, fallbackName: string) => {
  if (inFlight.has(path)) return;
  inFlight.add(path);
  notifyListeners();
  try {
    await fetchAndSave(path, fallbackName);
  } finally {
    inFlight.delete(path);
    notifyListeners();
  }
};

const segment = encodeURIComponent;

/** Each document's backend path, which is also its in-flight key. */
export const receiptDocumentPath = {
  order: (transactionId: string) =>
    `/transactions/${segment(transactionId)}/receipt.pdf`,
  donation: (donationRef: string) =>
    `/donations/${segment(donationRef)}/receipt.pdf`,
  charityBundle: (charityId: string, year: number) =>
    `/donations/charities/${segment(charityId)}/receipts.pdf?year=${year}`,
  summary: (year: number) => `/donations/years/${year}/summary.pdf`
} as const;

/** The merchant's receipt for a checkout's paying charge. */
export const downloadOrderReceipt = (transactionId: string): Promise<void> =>
  downloadPdf(
    receiptDocumentPath.order(transactionId),
    `receipt-${transactionId}.pdf`
  );

/** One donation's receipt, by its donation ref. */
export const downloadDonationReceipt = (donationRef: string): Promise<void> =>
  downloadPdf(
    receiptDocumentPath.donation(donationRef),
    `donation-receipt-${donationRef}.pdf`
  );

/** Every receipt for one charity in one year, as one PDF. */
export const downloadCharityReceipts = (
  charityId: string,
  year: number
): Promise<void> =>
  downloadPdf(
    receiptDocumentPath.charityBundle(charityId, year),
    `donation-receipts-${year}.pdf`
  );

/** The year's donation summary. */
export const downloadDonationSummary = (year: number): Promise<void> =>
  downloadPdf(
    receiptDocumentPath.summary(year),
    `donation-summary-${year}.pdf`
  );

interface ResolveReceiptCodeResponse {
  success: true;
  status: number;
  data: PublicReceiptResolution;
}

/**
 * What the printed receipt code opens. The route itself answers 200 `sign_in_required` when its
 * per-network limit is reached; only the backend's global throttler can answer 429, and that is
 * shown as the same sign-in answer.
 */
export const resolveReceiptCode = async (
  code: string
): Promise<PublicReceiptResolution> => {
  const payload: ResolveReceiptCodePayload = { code };
  try {
    const response = await apiClient.post<ResolveReceiptCodeResponse>(
      '/public/receipts/resolve',
      payload
    );
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 429) {
      return { result: 'sign_in_required' };
    }
    throw error;
  }
};
